import {
  Accordion,
  AccordionItem,
  Button,
  Modal,
} from "@appkit4/react-components";
import { saveAs } from "file-saver";
import { useState } from "react";
import toast from "../../../components/DismissibleToast";
import sqldatabaseService, {
  getWBSLogs,
} from "../../../services/sqldatabase/sqldatabase.service";
import { exportNotSubmittedReport } from "../../../utils/groupsReportOutput";
import {
  exportWBSLogs,
  exportWBSLogsFullReport,
} from "../../../utils/wbsOutput";
import { exportGroupReportingCompanies } from "../util/homeUtil";
import CopyGroupModal from "./CopyGroup";
import DeleteGroupModal from "./DeleteGroup";
import { exportGroups } from "./outputGroups";
import ReinstateResponseModal from "./ReinstateNonSelectedResponse";
import ReinstateResponseById from "./ReinstateResponseById";
const ExcelJS = require("exceljs");

const AdminPanel = ({
  visible,
  setVisible,
  userGroupList,
  fetchGroups,
  isSystemAdmin,
  isClientFacing,
}) => {
  const [activeKeys, setActiveKeys] = useState("");
  const [resetCopyGroupModal, setResetCopyGroupModal] = useState(false);
  const [resetDeleteGroupModal, setResetDeleteGroupModal] = useState(false);
  const [resetReinstateResponseModal, setResetReinstateResponseModal] =
    useState(false);
  const [resetReinstateByIdModal, setResetReinstateByIdModal] = useState(false);
  const [wbsLogsLoading, setWbsLogsLoading] = useState(false);
  const [wbsLogsFullLoading, setWbsLogsFullLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const onClickAccordion = (activeKeys) => {
    setActiveKeys(activeKeys);
    if (!activeKeys.includes("1")) {
      setResetCopyGroupModal(true);
    }

    if (!activeKeys.includes("2")) {
      setResetReinstateResponseModal(true);
    }

    if (!activeKeys.includes("9")) {
      setResetReinstateByIdModal(true);
    }
  };

  const handleExport = async () => {
    setWbsLogsLoading(true);
    try {
      const response = await getWBSLogs();

      const workbook = new ExcelJS.Workbook();
      exportWBSLogs(response.data, workbook);

      const excelFileName = "CIR Analyser - WBS Logs.xlsx";
      const buffer = await workbook.xlsx.writeBuffer();
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([buffer], { type: fileType });
      saveAs(blob, excelFileName);
    } catch (e) {
      console.error(e);
      toast.error(
        "Failed to download logs, check the console for more information",
      );
    } finally {
      setWbsLogsLoading(false);
    }
  };

  const exportFullWBSReport = async () => {
    setWbsLogsFullLoading(true);

    try {
      const response = await sqldatabaseService.getFullGroupReport();
      const workbook = new ExcelJS.Workbook();
      exportWBSLogsFullReport(response.data, workbook);

      const excelFileName = `CIR Analyser - WBS Logs Full Report ${new Date().toLocaleString().replace(",", " -")}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([buffer], { type: fileType });
      saveAs(blob, excelFileName);
    } catch (e) {
      console.error(e);
      toast.error(
        "Failed to download full report, check the console for more information",
      );
    } finally {
      setWbsLogsFullLoading(false);
    }
  };

  const downloadGroupList = async () => {
    setGroupsLoading(true);
    try {
      const response = await sqldatabaseService.getAllGroupReport();

      if (response?.status !== 200) {
        // set group lists that user does / doesn't have access to
        const message =
          (typeof response === "string"
            ? response
            : (response?.err?.message ?? response?.message)) || "Unknown Error";
        toast.error(`Failed to get group list :${message}`);
        return;
      }

      const { blob, excelFileName } = await exportGroups(response.data);
      saveAs(blob, excelFileName);
    } catch (e) {
      console.error(e);
      toast.error(
        "Failed to download groups, check the console for more information",
      );
    } finally {
      setGroupsLoading(false);
    }
  };

  const downloadPeriodsReport = async () => {
    setReportLoading(true);
    try {
      const response =
        await sqldatabaseService.getFinalisedNotSubmittedReport();

      if (response?.status !== 200) {
        // set group lists that user does / doesn't have access to
        const message =
          (typeof response === "string"
            ? response
            : (response?.err?.message ?? response?.message)) || "Unknown Error";
        toast.error(`Failed to get report of groups not submitted :${message}`);
        return;
      }
      const workbook = new ExcelJS.Workbook();
      exportNotSubmittedReport(response.data, workbook);

      const excelFileName = `CIR Analyser - Finalised not submitted Report ${new Date().toLocaleString().replace(",", " -")}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([buffer], { type: fileType });

      saveAs(blob, excelFileName);
    } catch (e) {
      console.error(e);
      toast.error(
        "Failed to generate the report, check the console for more information",
      );
    } finally {
      setReportLoading(false);
    }
  };

  const downloadReportingCompanyReport = async () => {
    setReportLoading(true);
    try {
      const response = await sqldatabaseService.getReportingCompaniesReport();

      if (response?.status !== 200) {
        const message =
          (typeof response === "string"
            ? response
            : (response?.err?.message ?? response?.message)) || "Unknown Error";
        toast.error(`Failed to get reporting companies report: ${message}`);
        return;
      }

      const workbook = new ExcelJS.Workbook();
      exportGroupReportingCompanies(response.data, workbook);

      const excelFileName = `CIR Analyser - Reporting Companies Report ${new Date()
        .toLocaleString()
        .replace(",", " -")}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([buffer], { type: fileType });

      saveAs(blob, excelFileName);
    } catch (e) {
      console.error(e);
      toast.error(
        "Failed to generate the report, check the console for more information",
      );
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <>
      <Modal
        initialFocus={false}
        visible={visible}
        title={"Admin Panel"}
        ariaLabel={"Admin Panel"}
        onCancel={() => setVisible(false)}
        maskCloseable={false}
        modalStyle={{ width: "70rem" }}
        footerStyle={{
          paddingTop: "8px",
          marginTop: "-8px",
          minHeight: "100px",
        }}
        header={""}
        icons={""}
      >
        <Accordion
          multiple={false}
          onClick={onClickAccordion}
          activeKeys={activeKeys}
        >
          <AccordionItem title={"Copy Group"} itemKey="1">
            <CopyGroupModal
              groupList={userGroupList}
              fetchGroups={fetchGroups}
              resetForm={resetCopyGroupModal}
              setResetForm={setResetCopyGroupModal}
              isClientFacing={isClientFacing}
            ></CopyGroupModal>
          </AccordionItem>
          <AccordionItem title={"Delete Group"} itemKey="2">
            <DeleteGroupModal
              groupList={userGroupList}
              fetchGroups={fetchGroups}
              resetForm={resetDeleteGroupModal}
              setResetForm={setResetDeleteGroupModal}
            ></DeleteGroupModal>
          </AccordionItem>
        </Accordion>
        {isSystemAdmin && !isClientFacing && (
          <Accordion>
            <AccordionItem
              title={"Reinstate Latest Non-Submitted Response"}
              itemKey="3"
            >
              <ReinstateResponseModal
                groupList={userGroupList}
                fetchGroups={fetchGroups}
                resetForm={resetReinstateResponseModal}
                setResetForm={setResetReinstateResponseModal}
              ></ReinstateResponseModal>
            </AccordionItem>
            <AccordionItem title={"WBS Logs"} itemKey="4">
              <Button onClick={handleExport} disabled={wbsLogsLoading}>
                {wbsLogsLoading ? "Downloading..." : "Download WBS Logs"}
              </Button>
            </AccordionItem>
            <AccordionItem title={"WBS Logs: Full Report"} itemKey="5">
              <Button
                onClick={exportFullWBSReport}
                loading={wbsLogsFullLoading}
              >
                Download WBS Log: Full report
              </Button>
              <p>Note: this can take a couple minutes</p>
            </AccordionItem>
            <AccordionItem title={"Group List"} itemKey="6">
              <Button onClick={downloadGroupList} loading={groupsLoading}>
                {groupsLoading ? "Downloading..." : "Download Group List"}
              </Button>
            </AccordionItem>
            <AccordionItem
              title={"Report for periods finalised but not submitted"}
              itemKey="7"
            >
              <Button onClick={downloadPeriodsReport} loading={reportLoading}>
                {reportLoading ? "Downloading..." : "Download periods report"}
              </Button>
            </AccordionItem>
            <AccordionItem title={"Get Reporting Companies Report"}>
              <Button
                onClick={downloadReportingCompanyReport}
                loading={reportLoading}
              >
                Download Reporting Companies Report
              </Button>
            </AccordionItem>
            <AccordionItem title={"Reinstate Response by ID"} itemKey="9">
              <ReinstateResponseById
                groupList={userGroupList}
                fetchGroups={fetchGroups}
                resetForm={resetReinstateByIdModal}
                setResetForm={setResetReinstateByIdModal}
              ></ReinstateResponseById>
            </AccordionItem>
          </Accordion>
        )}
      </Modal>
    </>
  );
};

export default AdminPanel;
